import { useEffect, useState } from "react";
import { PericiaMetricDTO } from "../../../../entities/pericia/model/dto";
import { UseGetPericiasMetricsProps, useGetPericiasMetrics } from "./useGetPericiasMetrics";
import { DAY_ITA, MONTH_ITA, WEEK_ITA } from "../../../../shared/constants/metric-chart-texts";

export const UsePericiasMetrics = () => {
  const [searchProps, setSearchProps] = useState<UseGetPericiasMetricsProps>({
    startDate: new Date(),
    endDate: new Date(),
    enabled: false
  });
  const { data, error, isLoading } = useGetPericiasMetrics({ ...searchProps });
  const [metrics, setMetrics] = useState<PericiaMetricDTO[]>([]);
  const [timeFrame, setTimeFrame] = useState<string>("");

  const metricsByDay = () => {
    if (!data) return;

    setMetrics(mergeByDay(data));
    setTimeFrame(`${DAY_ITA}-${data.length}`);
  };

  const metricsByWeek = () => {
    if (!data) return;

    const mergedMetrics = mergeByWeek(data);
    setTimeFrame(`${WEEK_ITA}-${mergedMetrics.length}`);
    setMetrics(mergedMetrics);
  };

  const metricsByMonth = () => {
    if (!data) return;
    const mergedMetrics = mergeByMonthAndYear(data);
    setTimeFrame(`${MONTH_ITA}-${mergedMetrics.length}`);
    setMetrics(mergedMetrics);
  };

  const handleSetSearchProps = (props: Partial<UseGetPericiasMetricsProps>) => {
    setSearchProps((prevState) => {
      return { ...prevState, ...props, enabled: props.enabled ? true : false };
    });
  };

  useEffect(() => {
    if (!data) return;

    const mergedMetrics = mergeByMonthAndYear(data);
    setMetrics(mergedMetrics);
    setTimeFrame(`${MONTH_ITA}-${mergedMetrics.length}`);
  }, [data]);

  return {
    error,
    isLoading,
    metrics,
    searchProps,
    timeFrame,
    createdInfo: createdInfo(metrics),
    finishedInfo: finishedInfo(metrics),
    billedInfo: billedInfo(metrics),
    metricsStartDate: data ? new Date(data[0].date).toLocaleDateString("pt-BR") : new Date().toLocaleDateString("pt-BR"),
    metricsEndDate: data ? new Date(data[data.length - 1].date).toLocaleDateString("pt-BR") : new Date().toLocaleDateString("pt-BR"),
    metricsByDay,
    metricsByWeek,
    metricsByMonth,
    handleSetSearchProps
  };
};

const mergeByDay = (data: PericiaMetricDTO[]): PericiaMetricDTO[] => {
  return data.map((item) => ({
    date: new Date(item.date).toLocaleDateString("pt-BR"),
    created: item.created,
    finished: item.finished,
    billed: item.billed
  }));
};

const mergeByMonthAndYear = (data: PericiaMetricDTO[]): PericiaMetricDTO[] => {
  const mergedMetricsMap: { [yearMonth: string]: PericiaMetricDTO } = {};

  data.forEach((metric) => {
    const [year, month] = metric.date.split("-");
    const yearMonth = `${year}-${month}`;
    console.log("debug", yearMonth);

    if (mergedMetricsMap[yearMonth]) {
      mergedMetricsMap[yearMonth].created += metric.created;
      mergedMetricsMap[yearMonth].finished += metric.finished;
      mergedMetricsMap[yearMonth].billed += metric.billed;
    } else {
      mergedMetricsMap[yearMonth] = { ...metric, date: yearMonth };
    }
  });

  const mergedMetrics: PericiaMetricDTO[] = Object.values(mergedMetricsMap);
  return mergedMetrics;
};

const getWeek = (date: Date): number => {
  const onejan = new Date(date.getFullYear(), 0, 1);
  const millisecsInDay = 86400000;
  return Math.ceil(((date.getTime() - onejan.getTime()) / millisecsInDay + onejan.getDay() + 1) / 7);
};

const mergeByWeek = (data: PericiaMetricDTO[]): PericiaMetricDTO[] => {
  return data.reduce((acc, curr) => {
    const date = new Date(curr.date);
    const week = getWeek(date);
    const year = date.getFullYear();
    const existing = acc.find((item) => item.date === `${year}-${week}`);
    if (existing) {
      existing.created += curr.created;
      existing.finished += curr.finished;
      existing.billed += curr.billed;
    } else {
      acc.push({ date: `${year}-${week}`, created: curr.created, finished: curr.finished, billed: curr.billed });
    }
    return acc;
  }, [] as PericiaMetricDTO[]);
};

const createdInfo = (metrics: PericiaMetricDTO[]) => {
  const totalCreated = metrics.reduce((acc, curr) => acc + curr.created, 0);
  return {
    totalCreated,
    average: (totalCreated / metrics.length).toFixed(2)
  };
};

const finishedInfo = (metrics: PericiaMetricDTO[]) => {
  const totalFinished = metrics.reduce((acc, curr) => acc + curr.finished, 0);
  return {
    totalFinished,
    average: (totalFinished / metrics.length).toFixed(2)
  };
};

const billedInfo = (metrics: PericiaMetricDTO[]) => {
  const totalBilled = metrics.reduce((acc, curr) => acc + curr.billed, 0);
  return {
    totalBilled,
    average: (totalBilled / metrics.length).toFixed(2)
  };
};
